/* eslint-disable react/prop-types, react/no-array-index-key */
import React, { createContext, useContext, useMemo, useState } from 'react';
import { isNil } from 'ramda';
import { NavLink } from 'react-router-dom';
import { useBreadCrumbs } from '@cyberint/common-apps/hooks/useBreadCrumbs';
import { useRouteTitle } from '@cyberint/common-apps/hooks/useRouteTitle';
import { ROUTES, useIsComponentCropped, isEmbedded, isSingleModeUi, useTenantDisplay } from '@cyberint/common-utils';
import { CustomTooltip, Icons } from '@cyberint/dsc';
import { t } from '../../i18n';
import * as S from './styled';

const BreadCrumbDepsContext = createContext(null);
const useBreadCrumbDeps = () => useContext(BreadCrumbDepsContext);

export const Crumb = ({ content, showSeparator, showBorder, isCandyBar, testId }) => {
  const { isCropped, componentRef } = useIsComponentCropped(content);
  if (isNil(content)) return null;

  return (
    <S.CrumbWrapper $border={showBorder} $isCandyBar={isCandyBar}>
      <CustomTooltip title={isCropped && <S.CrumbTooltipContent>{content}</S.CrumbTooltipContent>} width={250}>
        <S.CrumbContent ref={componentRef} data-testid={testId}>
          {content}
        </S.CrumbContent>
      </CustomTooltip>
      {showSeparator && <S.Separator />}
    </S.CrumbWrapper>
  );
};

const TenantCrumb = ({ isCandyBar }) => {
  const { TenantSelector } = useBreadCrumbDeps();
  return (
    /** Not relevant IMO for Pivot Mode */
    <S.CrumbWrapper $border $isCandyBar={isCandyBar}>
      <S.CrumbContent>
        <TenantSelector breadCrumbMode testId="input-tenants-breadcrumb" />
      </S.CrumbContent>
    </S.CrumbWrapper>
  );
};

const RegionCrumb = ({ isCandyBar }) => {
  const { RegionSelector } = useBreadCrumbDeps();
  return <RegionSelector breadCrumbMode isCandyBar={isCandyBar} testId="input-regions-breadcrumb" />;
};

const getRouteName = (routeName, match, customer, title) => {
  if (typeof routeName === 'function') {
    return routeName({ match, customer, title });
  }
  return routeName;
};

const InternalCrumb = ({ match, isLast, customer, pivotIndex, showSeparator, isCandyBar }) => {
  const { processedTenants } = useBreadCrumbDeps();
  const { title } = useRouteTitle(match);
  const { pathname, params, handle } = match;
  if (!handle) return null;
  const { crumb, routeName, testId } = handle;
  if (typeof crumb === 'function') {
    return crumb({ match, customer, isCandyBar, showSeparator, isLast });
  }
  if (crumb === false || crumb === null) {
    return null;
  }

  const { showAsLink, paramName } = crumb || {};
  const param = params[paramName];
  let displayText = param || getRouteName(routeName, match, customer, title);
  if (paramName === 'customerId') {
    displayText = processedTenants.find(tenant => tenant._id === param)?.name || displayText;
  }

  if (isNil(displayText)) return null;
  /** If it's last match, don't show it as link (even if asLink = true) */
  const state = pivotIndex != null ? { [ROUTES.DRILL_DOWN]: true, pivotIndex } : undefined;
  const testIdSuffix =
    testId || (typeof displayText === 'string' ? displayText : title)?.toLowerCase()?.replace(' ', '-');
  if ((showAsLink || pivotIndex != null) && !isLast) {
    return (
      <Crumb
        content={
          <NavLink to={pathname} state={state} data-testid={`link_${testIdSuffix}_breadcrumb`}>
            {displayText}
          </NavLink>
        }
        showSeparator={showSeparator ?? !isLast}
        isCandyBar={isCandyBar}
      />
    );
  }
  return (
    <Crumb
      content={displayText}
      showSeparator={showSeparator ?? !isLast}
      isCandyBar={isCandyBar}
      testId={`value_${testIdSuffix}_breadcrumb`}
    />
  );
};

const Pivot = ({ pivot, isLast, customer, pivotIndex, showSeparator }) =>
  pivot.match ? (
    <InternalCrumb
      match={pivot.match}
      pivotIndex={pivotIndex}
      customer={customer}
      isLast={isLast}
      showSeparator={showSeparator}
      isCandyBar
    />
  ) : (
    <span>{pivot.pathname}</span>
  );

const PivotsSelectors = ({ pivots, customer }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = e => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  return (
    <>
      <S.PivotListIcon onClick={handleClick}>
        <Crumb content="..." showSeparator isCandyBar />
      </S.PivotListIcon>
      <S.PivotsMenu open={!!anchorEl} onClose={() => setAnchorEl(null)} anchorEl={anchorEl}>
        <S.PivotsList>
          {pivots
            .map((pivot, idx) => (
              <Pivot
                key={`${idx}_${pivot.pathname}`}
                pivot={pivot}
                pivotIndex={idx - pivots.length}
                customer={customer}
                showSeparator={false}
              />
            ))
            .reverse()}
        </S.PivotsList>
      </S.PivotsMenu>
    </>
  );
};

const BreadCrumbsInner = () => {
  const { activeTenants, customer } = useBreadCrumbDeps();
  const showTenants = !isEmbedded || activeTenants?.length > 1;
  const { name: tenantName } = useTenantDisplay(customer);

  const { matches, pivots, closeBar } = useBreadCrumbs({ customer });
  const actualMatches = pivots.length > 1 ? pivots.map(dd => dd.match) : matches;
  const showCustomer =
    !isSingleModeUi() && showTenants && actualMatches?.some(m => m?.handle?.crumb?.showCustomer === true);
  return (
    <S.BreadCrumbWrapper>
      {pivots.length > 1 ? (
        <S.PivotsBarWrapper className="breadcrumb-bar">
          <S.PivotItems>
            {showCustomer && !isNil(customer) && <Crumb content={tenantName} showBorder isCandyBar />}
            <Pivot pivot={pivots[0]} pivotIndex={-(pivots.length - 1)} customer={customer} />
            {pivots.length > 2 && <PivotsSelectors pivots={pivots.slice(1, pivots.length - 1)} customer={customer} />}
            <Pivot pivot={pivots[pivots.length - 1]} customer={customer} isLast />
          </S.PivotItems>
          <S.ClosePivotsBar className="pivot-close" title={t('common.closePivot')}>
            <Icons.Close onClick={closeBar} />
          </S.ClosePivotsBar>
        </S.PivotsBarWrapper>
      ) : (
        !isSingleModeUi() && (
          <S.CrumbsWrapper className="breadcrumb-bar">
            {showCustomer && !isNil(customer) && <RegionCrumb />}
            {showCustomer && !isNil(customer) && <TenantCrumb />}
            {matches.map((match, index) => (
              <InternalCrumb
                key={match.pathname}
                match={match}
                customer={customer}
                isLast={index === matches.length - 1}
              />
            ))}
            {/* <S.RightCrumb><MaintenanceMessage /></S.RightCrumb> */}
          </S.CrumbsWrapper>
        )
      )}
    </S.BreadCrumbWrapper>
  );
};

export const BreadCrumbsBase = ({ activeTenants, processedTenants, TenantSelector, RegionSelector, customer }) => {
  const deps = useMemo(
    () => ({
      activeTenants,
      processedTenants,
      TenantSelector,
      RegionSelector,
      customer
    }),
    [activeTenants, processedTenants, TenantSelector, RegionSelector, customer]
  );

  return (
    <BreadCrumbDepsContext.Provider value={deps}>
      <BreadCrumbsInner />
    </BreadCrumbDepsContext.Provider>
  );
};
